/**
 * API Service Layer for DashboardMixed
 * Centralized API client for all backend calls
 */

import { auth } from './firebase/config';
import { getIdToken } from 'firebase/auth';
import { logger } from './utils/logger';

// Base URL for API - adjust based on environment
// Check if we're in production (not localhost)
const isProduction = window.location.hostname !== 'localhost' && 
                     window.location.hostname !== '127.0.0.1' &&
                     !window.location.hostname.includes('localhost');

// Get API URL from environment variable
const envApiUrl = import.meta.env.VITE_API_URL;

// Determine API base URL
let API_BASE_URL;
if (isProduction) {
    // In production, MUST use VITE_API_URL - fail loudly if not set
    if (!envApiUrl || envApiUrl === 'http://localhost:5000') {
        console.error('[ERROR] VITE_API_URL not set correctly in production build!');
        console.error('[ERROR] Current value:', envApiUrl);
        console.error('[ERROR] Hostname:', window.location.hostname);
        console.error('[ERROR] Please rebuild with: npm run build (after setting .env.production)');
        // Still try to use production URL as fallback
        API_BASE_URL = 'https://altradenative-api-1036144143428.us-central1.run.app';
    } else {
        API_BASE_URL = envApiUrl;
    }
} else {
    // Development: use env var or default to localhost
    API_BASE_URL = envApiUrl || 'http://localhost:5000';
}

/**
 * Helper function to handle API responses and errors
 */
async function fetchAPI(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const timeoutMs = 20000;
    
    // Build headers
    let headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };
    
    // Add Firebase token if user is logged in
    // This won't break anything if auth is disabled on backend
    let hasToken = false;
    let tokenError = null;
    try {
        const user = auth.currentUser;
        if (user) {
            try {
                // Do not force token refresh on every request.
                // Firebase will refresh only when needed and serve cached token otherwise.
                const token = await getIdToken(user);
                headers['Authorization'] = `Bearer ${token}`;
                hasToken = true;
            } catch (error) {
                tokenError = error.message;
                // Log error for debugging
                logger.error('Could not get auth token:', error.message);
            }
        }
    } catch (error) {
        tokenError = error.message;
        // Log error for debugging
        logger.error('Firebase auth error:', error.message);
    }
    
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const response = await fetch(url, {
            ...options,
            headers,
            signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
            // Handle 401 (Unauthorized) errors gracefully
            if (response.status === 401) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Authentication required. Please log in.');
            }
            const errorText = await response.text().catch(() => '');
            throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return { data, error: null };
    } catch (error) {
        if (error?.name === 'AbortError') {
            logger.error(`API call timeout for ${endpoint} after ${timeoutMs}ms`);
            return { data: null, error: 'Request timeout. Please try again.' };
        }
        logger.error(`API call failed for ${endpoint}:`, error);
        return { data: null, error: error.message };
    }
}

/**
 * Helper to build a query string from an object of params
 */
function buildQuery(params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value);
        }
    });
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
}

/**
 * Get risk profile metadata
 */
export async function getRiskProfiles(variant = 'hybrid') {
    const query = buildQuery({ variant });
    return await fetchAPI(`/api/mixed/risk-profiles${query}`);
}

/**
 * Get market status (open/closed)
 */
export async function getMarketStatus(variant = 'hybrid') {
    const query = buildQuery({ variant });
    return await fetchAPI(`/api/mixed/market-status${query}`);
}

/**
 * Get portfolio allocation for a specific risk profile
 * @param {string} risk - One of "Averse", "Neutral", or "Seeker"
 */
export async function getAllocation(risk = 'Averse', variant = 'hybrid') {
    const query = buildQuery({ risk, variant });
    return await fetchAPI(`/api/mixed/allocation${query}`);
}

/**
 * Get performance data for a specific risk profile
 * @param {string} risk - One of "Averse", "Neutral", or "Seeker"
 */
export async function getPerformance(risk = 'Averse', variant = 'hybrid') {
    const query = buildQuery({ risk, variant });
    return await fetchAPI(`/api/mixed/performance${query}`);
}

/**
 * Get live market quotes
 */
export async function getQuotes(variant = 'hybrid') {
    const query = buildQuery({ variant });
    return await fetchAPI(`/api/mixed/quotes${query}`);
}

/**
 * Get opportunity scores at different time points
 * @param {string} risk - One of "Averse", "Neutral", or "Seeker"
 */
export async function getOpportunityScores(risk = 'Averse', variant = 'hybrid') {
    const query = buildQuery({ risk, variant });
    return await fetchAPI(`/api/mixed/opportunity-scores${query}`);
}

/**
 * Get correlation data (scatter + regression) for a specific risk profile
 * @param {string} risk - One of "Averse", "Neutral", or "Seeker"
 */
export async function getCorrelation(risk = 'Averse', variant = 'hybrid') {
    const query = buildQuery({ risk, variant });
    return await fetchAPI(`/api/mixed/correlation${query}`);
}

/**
 * Get relative performance data for all risk profiles
 */
export async function getRelativePerformance(variant = 'hybrid') {
    const query = buildQuery({ variant });
    return await fetchAPI(`/api/mixed/relative-performance${query}`);
}

/**
 * Get volatility data for all risk profiles
 */
export async function getVolatility(variant = 'hybrid') {
    const query = buildQuery({ variant });
    return await fetchAPI(`/api/mixed/volatility${query}`);
}

/**
 * Get market context data (VIX, indicators, opportunity trends)
 * @param {string} risk - One of "Averse", "Neutral", or "Seeker"
 */
export async function getMarketContext(risk = 'Averse', variant = 'hybrid') {
    const query = buildQuery({ risk, variant });
    return await fetchAPI(`/api/mixed/market-context${query}`);
}

/**
 * Get exposure timeline data for the last 3 years
 * @param {string} risk - One of "Averse", "Neutral", or "Seeker"
 * @param {string} frequency - Data frequency: "daily" or "weekly" (default: "daily")
 */
export async function getExposureTimeline(risk = 'Averse', frequency = 'daily', variant = 'hybrid') {
    const query = buildQuery({ risk, frequency, variant });
    const url = `/api/mixed/exposure-timeline${query}`;
    logger.log(`[API] Requesting exposure timeline:`, { risk, frequency, url });
    const result = await fetchAPI(url);
    if (result.data && result.data.timeline) {
        const timeline = result.data.timeline;
        logger.log(`[API] Received ${timeline.length} data points for exposure timeline`);
        
        // Validate data frequency
        if (timeline.length >= 2) {
            const firstDate = new Date(timeline[0].date);
            const secondDate = new Date(timeline[1].date);
            const daysDiff = Math.round((secondDate - firstDate) / (1000 * 60 * 60 * 24));
            
            if (frequency === 'daily' && daysDiff > 1) {
                logger.error(`[API] WARNING: Requested daily data but received ${daysDiff}-day intervals!`);
                logger.error(`[API] First two dates: ${timeline[0].date} -> ${timeline[1].date}`);
                logger.error(`[API] This indicates the backend may not support frequency=daily parameter`);
            } else if (frequency === 'daily' && daysDiff === 1) {
                logger.log(`[API] ✓ Confirmed daily data: ${daysDiff}-day intervals`);
            }
        }
        
        if (timeline.length > 0) {
            logger.log(`[API] Date range: ${timeline[0].date} to ${timeline[timeline.length - 1].date}`);
        }
    } else if (result.error) {
        logger.error(`[API] Error fetching exposure timeline:`, result.error);
    }
    return result;
}

/**
 * Batch endpoint to fetch multiple endpoints in a single request
 * @param {Array<string>} endpoints - Array of endpoint names to fetch
 * @param {string} risk - Risk profile (required for risk-dependent endpoints)
 * @param {string} variant - Dashboard variant (default: 'hybrid')
 * @param {string} frequency - Frequency for exposure-timeline (default: 'daily')
 * @returns {Promise<{data: Object|null, error: string|null}>}
 */
export async function getBatchData(endpoints = [], risk = 'Averse', variant = 'hybrid', frequency = 'daily') {
    if (!endpoints || endpoints.length === 0) {
        return { data: null, error: 'At least one endpoint must be specified' };
    }
    
    const query = buildQuery({
        endpoints: endpoints.join(','),
        risk,
        variant,
        frequency
    });
    const result = await fetchAPI(`/api/mixed/batch${query}`);
    return result;
}

/**
 * Health check endpoint
 */
export async function healthCheck() {
    return await fetchAPI('/health');
}

